import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { useAuth } from '../../contexts/AuthContext';
import { claimNotes } from '../../services/notesService';

/**
 * Component to handle OAuth callback redirects
 * This component is rendered at the /auth/callback route
 */
const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const { checkAuthStatus } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Handle the auth callback and token parsing
    const processAuthCallback = async () => {
      try {
        // Check auth status to update context with user data
        await checkAuthStatus();

        // Track successful login with Google Analytics
        ReactGA.event('login', { method: 'Google' });

        // Check for pending note from localStorage
        const pendingNoteId = localStorage.getItem('pendingNoteId');

        // Claim the pending note if exists
        if (pendingNoteId) {
          try {
            await claimNotes(pendingNoteId);
          } catch (claimErr) {
            console.error('Error claiming pending note:', claimErr);
          }
          localStorage.removeItem('pendingNoteId');
        }

        // Always try email-based claim for older notes
        try {
          await claimNotes();
        } catch (claimErr) {
          console.error('Error claiming notes by email:', claimErr);
        }

        // Redirect to My Notes on success
        navigate('/my-notes');
      } catch (err) {
        console.error('Error processing auth callback:', err);
        setError('Authentication failed. Please try again.');
      }
    };

    processAuthCallback();
  }, [checkAuthStatus, navigate]);

  if (error) {
    return (
      <div className="auth-callback-error">
        <h3>Authentication Error</h3>
        <p>{error}</p>
        <button onClick={() => navigate('/login')}>Return to Login</button>
      </div>
    );
  }

  return (
    <div className="auth-callback-loading">
      <p>Completing authentication...</p>
      {/* Consider adding a loading spinner here */}
    </div>
  );
};

export default AuthCallback; 